import { useQueryClient } from "@tanstack/react-query";
import { object, string } from "prop-types";
import { Suspense, lazy, memo, useCallback, useEffect } from "react";

import styled from "styled-components/macro";
import { MAX_MODAL_PX_HEIGHT, MAX_MODAL_VH } from "theme/interpolations/height";

import { useOverlayModalIsOpen } from "Flocknote/GlobalComponents/OverlayModal/overlayModalStore";
import flocknoteStateHooks from "Flocknote/stateHooks";

import { dashboardKeys } from "Dashboard/API/keys";

import FnrCard from "UI/FnrCard/FnrCard";
import FnrCloseButton, { CLOSE_BUTTON_DEFAULT_MARGIN } from "UI/FnrCloseButton";
import { ICON_BUTTON_SIZE } from "UI/FnrIconButton/FnrIconButton";
import FnrModal from "UI/FnrModal";
import FnrOpacityToggle from "UI/FnrOpacityToggle";
import RunningUno from "UI/RunningUno";
import { scrollbarInvisibleThin } from "UI/scrollbarStyles";

import useEscapeKey from "Utilities/Hooks/useEscapeKey";

import configsByContentKey from "./configsByContentKey";
import {
  ADD_BATCH_GIFT,
  ADD_TO_PARISH,
  ADD_PAYMENT_METHOD,
  ARCHIVE_FUND,
  ARCHIVE_USER,
  ASK_THE_MEMBER,
  BILLING_LOCKOUT,
  CAMPAIGNS_SPLASH,
  CAMPAIGN_WIZARD,
  CANCEL_FLOCKNOTE_COMPLETE,
  CONTACT_HISTORY,
  CREATE_ADDON_GIFT,
  CREATE_FUND,
  CREATE_GIVING_HOUSEHOLD,
  CREATE_GROUP,
  CREATE_PLEDGE,
  CREATE_PLEDGE_OVERLAY,
  CREATE_PRAYER_REQUEST,
  CTA_EDITOR,
  DISCOUNT_DETAIL_MODAL,
  DONOR_SUMMARY_REPORTS,
  EDIT_PLEDGE,
  EMERGENCY_CONTACT_HISTORY,
  EXTENDED_FIELDS,
  EXTENDED_FIELD_HISTORY,
  EVERYONE_REMOVE_MEMBER_CONFIRMATION,
  FAMILY_REGISTRATION_SPLASH,
  FAMILY_RELATIONSHIP_PICKER,
  FNC_SMART_GROUP_PROMO,
  FNC_SMART_GROUP_UPGRADE_GROUP_LIMIT,
  FNC_UPGRADE,
  GIFT_REFUND,
  GIVING_SPLASH,
  IMPORTING,
  LEGAL_ENTITY_INFO,
  MAKE_SUBGROUP,
  MANAGE_MY_GIVING_SPLASH,
  MANAGE_PROXY_CONTACTS,
  MEDICAL_ALLERGY_HISTORY,
  NEW_CAMPAIGN,
  PARENT_GROUP_PERMISSION_CONFLICT,
  PAYMENT_DATES,
  PLEDGE_DETAIL,
  PLEDGE_HISTORY,
  PREPARING_EXPORT,
  RE_SETUP_WIZARD,
  SACRAMENT_HISTORY,
  SACRAMENT_TRACKING,
  SMALL_CHURCH_ATTESTATION,
  STAX_KYC,
  STAX_STATUS,
  TRANSFER_PARTIAL_CONTACT_VIEW_IN_SHEET,
  TTJ_ACTION_FLOW,
  TWO_FA_DISENROLLMENT,
  TWO_FA_ENROLLMENT,
} from "./contentKeys";
import useModalStore, {
  useContentIsLazyLoading,
  useLongLazyLoad,
} from "./modalStore";

const ModalContents = memo(({ contentKey, contentProps }) => {
  let Component;
  // Add additional case statements here for the components you'd like to add.
  switch (contentKey) {
    case ADD_BATCH_GIFT:
      Component = lazy(() =>
        import("Giving/Designations/tabs/AddBatchGiftModal")
      );
      break;
    case ADD_TO_PARISH:
      Component = lazy(() =>
        import(
          "HouseholdProfile/components/HouseholdProfileInfo/components/PrimaryNetworkSelector/AddToParish/components/AddToParishModal"
        )
      );
      break;
    case ARCHIVE_FUND:
      Component = lazy(() =>
        import(
          "Network/SettingsPanels/GivingFundSettingsPanel/ArchiveFundModal"
        )
      );
      break;
    case BILLING_LOCKOUT:
      Component = lazy(() =>
        import("CrossFeatureComponents/BillingLockout/BillingLockout")
      );
      break;
    case EXTENDED_FIELDS:
      Component = lazy(() => import("ExtendedFields/ExtendedFields"));
      break;
    case CREATE_ADDON_GIFT:
      Component = lazy(() => import("Giving/CreateAddOnGift"));
      break;
    case CREATE_FUND:
      Component = lazy(() =>
        import("Giving/Designations/components/NewFundModal")
      );
      break;
    case CREATE_GIVING_HOUSEHOLD:
      Component = lazy(() =>
        import("Giving/CreateGivingHousehold/CreateGivingHousehold")
      );
      break;
    case CTA_EDITOR:
      Component = lazy(() => import("Giving/CtaEditor"));
      break;
    case STAX_KYC:
      Component = lazy(() => import("Giving/StaxKYC"));
      break;
    case STAX_STATUS:
      Component = lazy(() => import("Giving/StaxStatus"));
      break;
    case GIFT_REFUND:
      Component = lazy(() =>
        import("Giving/GiftRefundOverlay/GiftRefundOverlay")
      );
      break;
    case FNC_UPGRADE:
      Component = lazy(() => import("Giving/FNCUpgradeOverlay"));
      break;
    case SACRAMENT_HISTORY:
      Component = lazy(() => import("History/SacramentHistory"));
      break;
    case SACRAMENT_TRACKING:
      Component = lazy(() => import("SacramentTracking/SacramentForm"));
      break;
    case ARCHIVE_USER:
      Component = lazy(() =>
        import("SacramentTracking/ArchiveUserConfirmation")
      );
      break;
    case TWO_FA_ENROLLMENT:
      Component = lazy(() => import("UserProfile/TwoFAEnrollment"));
      break;
    case TWO_FA_DISENROLLMENT:
      Component = lazy(() => import("UserProfile/TwoFA/TwoFADisenrollment"));
      break;
    case MANAGE_PROXY_CONTACTS:
      Component = lazy(() => import("ManageProxyContacts/ManageProxyContacts"));
      break;
    case FAMILY_RELATIONSHIP_PICKER:
      Component = lazy(() =>
        import("FamilyRelationshipPicker/FamilyRelationshipPicker")
      );
      break;
    case DONOR_SUMMARY_REPORTS:
      Component = lazy(() =>
        import("Giving/TaxSummaryEOY/DonorSummaryReports")
      );
      break;
    case EXTENDED_FIELD_HISTORY:
      Component = lazy(() => import("History/ExtendedFieldHistory"));
      break;
    case EVERYONE_REMOVE_MEMBER_CONFIRMATION:
      Component = lazy(() =>
        import(
          "CrossFeatureComponents/EveryoneRemoveMemberConfirmation/EveryoneRemoveMemberConfirmation"
        )
      );
      break;
    case PARENT_GROUP_PERMISSION_CONFLICT:
      Component = lazy(() =>
        import(
          "CrossFeatureComponents/ParentGroupPermissionConflict/ParentGroupPermissionConflict"
        )
      );
      break;
    case CONTACT_HISTORY:
      Component = lazy(() => import("History/ContactHistory"));
      break;
    case ASK_THE_MEMBER:
      Component = lazy(() =>
        import(
          "CrossFeatureComponents/AskTheMember/AskTheMemberModal/AskTheMemberModal"
        )
      );
      break;
    case CREATE_GROUP:
      Component = lazy(() =>
        import("CrossFeatureComponents/CreateGroup/CreateGroup")
      );
      break;
    case FNC_SMART_GROUP_PROMO:
      Component = lazy(() => import("Group/FNCSmartGroupPromo"));
      break;
    case FAMILY_REGISTRATION_SPLASH:
      Component = lazy(() => import("splashes/FamilyRegistrationSplash"));
      break;
    case GIVING_SPLASH:
      Component = lazy(() => import("splashes/GivingSplash"));
      break;
    case CAMPAIGNS_SPLASH:
      Component = lazy(() => import("splashes/CampaignsSplash"));
      break;
    case NEW_CAMPAIGN:
      Component = lazy(() =>
        import("Giving/Campaigns/NewCampaignModal/NewCampaignModal")
      );
      break;
    case CAMPAIGN_WIZARD:
      Component = lazy(() =>
        import("Giving/Designations/CampaignWizard/CampaignWizard")
      );
      break;
    case MANAGE_MY_GIVING_SPLASH:
      Component = lazy(() => import("splashes/ManageMyGivingSplash"));
      break;
    case CREATE_PLEDGE:
      Component = lazy(() => import("Giving/Pledging/PledgeContent"));
      break;
    case CREATE_PLEDGE_OVERLAY:
      Component = lazy(() =>
        import(
          "CrossFeatureComponents/PledgeManagement/components/CreatePledgeOverlay"
        )
      );
      break;
    case PLEDGE_DETAIL:
      Component = lazy(() =>
        import("Giving/Designations/components/PledgeDetailModal")
      );
      break;
    case PAYMENT_DATES:
      Component = lazy(() =>
        import(
          "Giving/Pledging/PledgePagePublic/panels/Submit/GiftBar/PaymentDatesModal"
        )
      );
      break;
    case ADD_PAYMENT_METHOD:
      Component = lazy(() =>
        import(
          "Giving/Pledging/PledgePagePublic/panels/Submit/AddPaymentMethodModal"
        )
      );
      break;
    case EDIT_PLEDGE:
      Component = lazy(() =>
        import(
          "CrossFeatureComponents/PledgeManagement/components/EditPledgeOverlay"
        )
      );
      break;
    case PLEDGE_HISTORY:
      Component = lazy(() =>
        import(
          "CrossFeatureComponents/PledgeManagement/components/PledgeHistoryOverlay"
        )
      );
      break;
    case FNC_SMART_GROUP_UPGRADE_GROUP_LIMIT:
      Component = lazy(() =>
        import("Group/FNCSmartGroupPromo/FNCSmartGroupUpgrade")
      );
      break;
    case PREPARING_EXPORT:
      Component = lazy(() => import("Export/PreparingExportModal"));
      break;
    case LEGAL_ENTITY_INFO:
      Component = lazy(() =>
        import("CrossFeatureComponents/LegalEntityInfo/LegalEntityInfo")
      );
      break;
    case CREATE_PRAYER_REQUEST:
      Component = lazy(() =>
        import(
          "PrayerRequests/components/CreatePrayerRequest/CreatePrayerRequest"
        )
      );
      break;
    case SMALL_CHURCH_ATTESTATION:
      Component = lazy(() => import("NumberManagement/SmallChurchAttestation"));
      break;
    case CANCEL_FLOCKNOTE_COMPLETE:
      Component = lazy(() =>
        import("Billing/CancelFlocknoteComplete/CancelFlocknoteComplete")
      );
      break;
    case TTJ_ACTION_FLOW:
      Component = lazy(() =>
        import("CrossFeatureComponents/TTJActionFlow/TTJActionFlow")
      );
      break;
    case IMPORTING:
      Component = lazy(() => import("Importing"));
      break;
    case TRANSFER_PARTIAL_CONTACT_VIEW_IN_SHEET:
      Component = lazy(() =>
        import("HappeTools/Transfers/components/ViewInSheet/ViewInSheet")
      );
      break;
    case MAKE_SUBGROUP:
      Component = lazy(() =>
        import("GroupsManagement/components/Modal/MakeSubgroupModal")
      );
      break;
    case RE_SETUP_WIZARD:
      Component = lazy(() =>
        import("RESetup/components/WizardLayout/WizardContainer")
      );
      break;
    case EMERGENCY_CONTACT_HISTORY:
      Component = lazy(() => import("History/EmergencyContactHistory"));
      break;
    case MEDICAL_ALLERGY_HISTORY:
      Component = lazy(() => import("History/MedicalAllergyHistory"));
      break;
    case DISCOUNT_DETAIL_MODAL:
      Component = lazy(() =>
        import(
          "Signups/SignupView/SignupSheet/Screens/FundIt/DiscountsFees/DiscountDetailModal/DiscountDetailModal"
        )
      );
      break;
    default:
      Component = () => null;
  }

  const setContentIsLazyLoading = useModalStore(
    (s) => s.setContentIsLazyLoading
  );

  const setLongLazyLoad = useModalStore((s) => s.setLongLazyLoad);

  const onLoaderMount = useCallback(() => {
    setContentIsLazyLoading(true);
  }, [setContentIsLazyLoading]);

  const onLoaderUnmount = useCallback(() => {
    setContentIsLazyLoading(false);
    setLongLazyLoad(false);
  }, [setContentIsLazyLoading, setLongLazyLoad]);

  const onTakingALongtime = useCallback(() => {
    setLongLazyLoad(true);
  }, [setLongLazyLoad]);

  return (
    <Suspense
      fallback={
        <RunningUno
          onMount={onLoaderMount}
          onUnmount={onLoaderUnmount}
          onTakingALongtime={onTakingALongtime}
        />
      }
    >
      <Component {...contentProps} isModal />
    </Suspense>
  );
});

ModalContents.propTypes = { contentKey: string, contentProps: object };

const overflow = ({ $flexHeight, $noMaxHeight, $scrollable }) => {
  if ($noMaxHeight) return `overflow: visible;`;
  if ($flexHeight || $scrollable) return `overflow-y: auto;`;
  return `overflow-y: hidden;`;
};

const MAX_MODAL_VW = "calc(100vw - 16px)";
const MAX_MODAL_PX_WIDTH = "800px";

const ContentContainer = styled(FnrCard)`
  ${({
    theme: {
      palette: { grey },
    },
    $flexHeight,
    $noMaxHeight,
    $flexWidth,
    $maxWidth = MAX_MODAL_PX_WIDTH,
    $maxHeight = `${MAX_MODAL_PX_HEIGHT}px`,
    $minHeight,
    $overflowY,
    $borderRadius,
    $legacy,
  }) => `
  ${
    $flexWidth
      ? `max-width: ${MAX_MODAL_VW};`
      : `width: min(${$maxWidth}, ${MAX_MODAL_VW});`
  }
  ${
    !$noMaxHeight && !$flexHeight
      ? `height: min(${$maxHeight}, ${MAX_MODAL_VH}vh); --modal-content-height: min(${$maxHeight}, ${MAX_MODAL_VH}vh);`
      : ``
  }
  ${$legacy ? "margin: 8px;" : ""}
  ${$minHeight ? `min-height: ${$minHeight};` : ""}
  ${$borderRadius ? `border-radius: ${$borderRadius};` : ""}
  ${
    !$noMaxHeight
      ? `max-height: calc(var(--full-viewport-height, 100vh) - 16px);`
      : ``
  }
  background-color: ${grey};`}
  position: relative;
  ${overflow}
  ${scrollbarInvisibleThin}
  scrollbar-width: none;
  ${({ $fullScreenOnMobile }) =>
    $fullScreenOnMobile &&
    `
    @media (max-width: 680px) {
      width: calc(100vw - 16px);
      max-width: calc(100vw - 16px);
      height: calc(100dvh - 16px);
      max-height: calc(100dvh - 16px);
      min-height: 0;
      margin: 0;
      padding: 0;
    }
  `}
`;

const Modal = memo(() => {
  const [{ isOpen, contentKey, contentProps }, { closeModal }] =
    flocknoteStateHooks.useModal();
  const isLazyLoading = useContentIsLazyLoading();
  const isLongLazyLoad = useLongLazyLoad();
  const queryClient = useQueryClient();

  const overlayIsOpen = useOverlayModalIsOpen();
  const preventClose = useModalStore((s) => s.preventClose);
  const setPreventClose = useModalStore((s) => s.setPreventClose);
  const transitionType = useModalStore((s) => s.transitionType);
  const setTransitionType = useModalStore((s) => s.setTransitionType);

  useEffect(() => {
    if (!isOpen) setPreventClose(false);
  }, [isOpen, setPreventClose]);

  const handleFinishedClosing = useCallback(() => {
    setTransitionType(null);
  }, [setTransitionType]);

  const handleClose = useCallback(() => {
    if (preventClose) return;
    // pass in onClose as a special contentProp to fire whatever you need when the modal closes:
    // openModal('someKey',' {onClose: () => {}})`
    if (contentProps?.onClose) contentProps.onClose();
    queryClient.invalidateQueries({ queryKey: dashboardKeys.nextActions() });
    setTransitionType(null);
    closeModal();
  }, [preventClose, contentProps, closeModal, queryClient, setTransitionType]);

  const handleBackgroundClick = (event) => {
    // Only a click on this backdrop itself — not inside the content, and not
    // on a nested FnrModal's backdrop, whose clicks bubble up the React tree.
    if (event.target === event.currentTarget) {
      if (configsByContentKey.closeOnBackgroundClick[contentKey]) {
        setTransitionType(null);
        closeModal();
      }
    }
  };
  useEscapeKey(handleClose, isOpen);

  const flexHeight = configsByContentKey.flexHeight[contentKey];
  const flexWidth = configsByContentKey.flexWidth[contentKey];
  const maxWidth = configsByContentKey.maxWidth[contentKey];
  const maxHeight = configsByContentKey.maxHeight[contentKey];
  const minHeight = configsByContentKey.minHeight[contentKey];
  const legacy = configsByContentKey.legacy[contentKey];
  const closeButtonPosition =
    configsByContentKey.closeButtonPosition[contentKey];
  const overflowY = configsByContentKey.overflowY[contentKey];
  const borderRadius = configsByContentKey.borderRadius[contentKey];
  const closeButtonMargin = borderRadius
    ? parseInt(borderRadius) - ICON_BUTTON_SIZE / 2
    : CLOSE_BUTTON_DEFAULT_MARGIN;
  const noMaxHeight = Boolean(configsByContentKey.noMaxHeight[contentKey]);
  const scrollable = Boolean(configsByContentKey.scrollable?.[contentKey]);
  const fullScreenOnMobile = Boolean(
    configsByContentKey.fullScreenOnMobile?.[contentKey]
  );

  return (
    <FnrModal
      show={isOpen}
      showInvisibly={isLazyLoading && !isLongLazyLoad}
      scaleDown={overlayIsOpen}
      custom={transitionType}
      onFinishedClosing={handleFinishedClosing}
      data-cy="Global_Modal"
      onBackgroundClick={handleBackgroundClick}
    >
      {closeButtonPosition === "hide" ? null : (
        <FnrOpacityToggle show={!preventClose}>
          <FnrCloseButton
            onClick={handleClose}
            aria-label="close-modal"
            legacy={legacy}
            position={closeButtonPosition}
            $margin={closeButtonMargin}
          />
        </FnrOpacityToggle>
      )}
      <ContentContainer
        data-cy="Modal_Container"
        $flexHeight={flexHeight}
        $flexWidth={flexWidth}
        $maxWidth={maxWidth}
        $maxHeight={maxHeight}
        $noMaxHeight={noMaxHeight}
        $minHeight={minHeight}
        $overflowY={overflowY}
        $borderRadius={borderRadius}
        $scrollable={scrollable}
        $fullScreenOnMobile={fullScreenOnMobile}
        variant="modal"
        $legacy={legacy}
      >
        <ModalContents contentProps={contentProps} contentKey={contentKey} />
      </ContentContainer>
    </FnrModal>
  );
});

export { MAX_MODAL_VW, MAX_MODAL_PX_WIDTH };
export default Modal;
